import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Menu,
  MenuItem,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { resetClientInfo } from "../../../redux/clientSlice";
import { resetClientData } from "../../../redux/clientDataSlice";
import { persistor } from "../../../redux/store";
import { supabase } from "../../../supabaseClient"; // Import Supabase client
import QuidproLogo from "./assets/quidproLowerLogo.png";
import { useAuth } from "../../../hooks/useAuth";
import BlueDropdown from "./assets/blueDropdown.png";
import NotificationIcon from "./assets/notificationIcon.png";
import DownIcon from "./assets/downIcon.png";
import "./homeHeader.css";

const menuItemStyles = {
  fontFamily: "Poppins",
  fontSize: "16px",
  color: "#333333",
  fontWeight: 400,
  lineHeight: "100%",
  letterSpacing: "-0.17px",
  padding: "15px 20px", // Ensure adequate padding
  border: "2px solid transparent", // Transparent by default
  borderRadius: "4px", // Optional: soften corners
  transition: "border 0.2s ease, background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#fff", // or "#e0e0e0", depending on the look you want
    border: "2px solid #144A8C", // Blue border on hover
  },
};

const menuItemStylesAuth = () => ({
  fontFamily: "Poppins",
  fontWeight: 400,
  fontSize: "18px",
  lineHeight: "130%",
  letterSpacing: "-0.17px",
  textAlign: "center",
  verticalAlign: "middle",
  color: "#333333",
  px: "30px",
  pr: "48px",
  py: "16px",
  mx: "20px",
  my: "10px",
  borderRadius: "8px",
});

const HomeHeader = ({
  openLoginPopup,
  title,
  isTitle,
  headerColor,
  navItems = false, // ← new prop
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuAnchorLoggedIn, setMenuAnchorLoggedIn] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false); // mobile drawer state
  const { isAuthenticated, loading } = useAuth();
  const leftNavItems = [
    { label: "Blog", path: "/blog" },
    { label: "List your firm on Quidpro", path: "/grow/attorney" },
    { label: "Contact us", path: "/contact-us" },
  ];

  // Retrieve client data and status from Redux
  const status = useSelector((state) => state.clientData.status);
  const name = useSelector((state) => {
    const client = state.clientData.clientData;
    return client
      ? `${client.first_name || ""} ${client.last_name || ""}`.trim()
      : "";
  });
  const menuRef = useRef(null);
  const menuRefLoggedIn = useRef(null);

  const handleMenuClick = (event) => setMenuAnchor(event.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  const handleMenuClickLoggedIn = (event) =>
    setMenuAnchorLoggedIn(event.currentTarget);
  const handleMenuCloseLoggedIn = () => setMenuAnchorLoggedIn(null);

  const handleLogoClick = () => navigate("/");

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      dispatch(resetClientInfo());
      dispatch(resetClientData());
      await persistor.purge();
      navigate("/");
    } catch (error) {
      console.error("Error logging out:", error.message);
      alert("Failed to log out. Please try again.");
    } finally {
      handleMenuCloseLoggedIn();
    }
  };

  const headerClass = headerColor
    ? `home-page-header ${headerColor}`
    : "home-page-header";

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        handleMenuClose();
      }
      if (
        menuRefLoggedIn.current &&
        !menuRefLoggedIn.current.contains(event.target)
      ) {
        handleMenuCloseLoggedIn();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Mobile drawer content
  const drawer = (
    <div className="mobile-drawer">
      <List>
        {leftNavItems.map((item) => (
          <ListItem
            button
            key={item.path}
            onClick={() => {
              navigate(item.path);
              handleDrawerToggle();
            }}
            className="mobile-drawer-item"
          >
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontFamily: "Poppins",
                fontSize: "18px",
                fontWeight: 600,
                letterSpacing: "0.5px",
              }}
            />
          </ListItem>
        ))}
        <hr />
        {status === "online" ? (
          <>
            <ListItem
              button
              onClick={() => {
                navigate("/client/my-appointments");
                handleDrawerToggle();
              }}
              className="mobile-drawer-item"
            >
              <ListItemText
                primary="My Appointments"
                primaryTypographyProps={{
                  fontFamily: "Poppins",
                  fontSize: "18px",
                  fontWeight: 600,
                }}
              />
            </ListItem>
            <ListItem
              button
              onClick={() => {
                navigate("/client/my-profile");
                handleDrawerToggle();
              }}
              className="mobile-drawer-item"
            >
              <ListItemText
                primary="My Profile"
                primaryTypographyProps={{
                  fontFamily: "Poppins",
                  fontSize: "18px",
                  fontWeight: 600,
                }}
              />
            </ListItem>
            <ListItem
              button
              onClick={handleLogout}
              className="mobile-drawer-item"
            >
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  fontFamily: "Poppins",
                  fontSize: "18px",
                  fontWeight: 600,
                }}
              />
            </ListItem>
          </>
        ) : (
          <>
            <ListItem
              button
              onClick={() => {
                openLoginPopup();
                handleDrawerToggle();
              }}
              className="mobile-drawer-item"
            >
              <ListItemText
                primary="Log in (Clients)"
                primaryTypographyProps={{
                  fontFamily: "Poppins",
                  fontSize: "18px",
                  fontWeight: 600,
                }}
              />
            </ListItem>
            <ListItem
              button
              onClick={() => {
                navigate("/grow/email");
                handleDrawerToggle();
              }}
              className="mobile-drawer-item"
            >
              <ListItemText
                primary="Log in (Lawyers)"
                primaryTypographyProps={{
                  fontFamily: "Poppins",
                  fontSize: "18px",
                  fontWeight: 600,
                }}
              />
            </ListItem>
            <ListItem
              button
              onClick={() => {
                navigate("/createuser/auth");
                handleDrawerToggle();
              }}
              className="mobile-drawer-item"
            >
              <ListItemText
                primary="Sign up"
                primaryTypographyProps={{
                  fontFamily: "Poppins",
                  fontSize: "18px",
                  fontWeight: 600,
                }}
              />
            </ListItem>
          </>
        )}
      </List>
    </div>
  );

  return (
    <div>
      <div className={headerClass}>
        <div className="home-header-left">
          {isTitle ? (
            <h1 className="home-header-title">{title}</h1>
          ) : (
            <img
              src={QuidproLogo}
              style={{ height: "30px" }}
              alt="Quidpro Logo"
              onClick={handleLogoClick}
            />
          )}
        </div>

        <div className="home-header-right">
          {/* Mobile hamburger icon */}
          <IconButton
            className="mobile-menu-button"
            onClick={handleDrawerToggle}
            sx={{ display: { xs: "block", md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Desktop nav & user info */}
          {navItems && (
            <nav className="home-header-nav">
              {leftNavItems.map((item) => (
                <span
                  key={item.path}
                  className="home-header-nav-item"
                  onClick={() => navigate(item.path)}
                  style={{ cursor: "pointer", marginRight: 20 }}
                >
                  {item.label}
                </span>
              ))}
            </nav>
          )}
          {status === "online" ? (
            <div className="home-user-info">
              <div className="home-login-dropdown">
                <img
                  src={NotificationIcon}
                  style={{ width: "24px", height: "24px" }}
                  alt="dropdown-icon"
                />
              </div>
              <div
                className="home-login-dropdown"
                onClick={handleMenuClickLoggedIn}
              >
                <p className="logged-in-name">{name}</p>
                <img
                  src={DownIcon}
                  style={{ width: "24px", height: "24px" }}
                  alt="dropdown-icon"
                />
              </div>

              <Menu
                anchorEl={menuAnchorLoggedIn}
                open={Boolean(menuAnchorLoggedIn)}
                onClose={handleMenuCloseLoggedIn}
                anchorOrigin={{ vertical: 50, horizontal: 120 }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                MenuListProps={{ sx: { borderRadius: 2, minWidth: 180 } }}
                ref={menuRefLoggedIn}
              >
                <MenuItem
                  onClick={() => navigate("/client/my-appointments")}
                  sx={menuItemStylesAuth()}
                >
                  My Appointments
                </MenuItem>
                <MenuItem
                  onClick={() => navigate("/client/my-profile")}
                  sx={menuItemStylesAuth()}
                >
                  My Profile
                </MenuItem>
                <MenuItem sx={menuItemStylesAuth()}>Settings</MenuItem>
                <hr style={{ width: "80%", justifySelf: "center" }} />
                <MenuItem onClick={handleLogout} sx={menuItemStylesAuth()}>
                  Logout
                </MenuItem>
              </Menu>
            </div>
          ) : (
            <div className="home-login-signup">
              <div className="home-login-dropdown" onClick={handleMenuClick}>
                Log in
                <img
                  src={BlueDropdown}
                  style={{ width: "24px", height: "24px" }}
                  alt="dropdown-icon"
                />
              </div>

              <div
                className="home-signup-button"
                onClick={() => navigate("/createuser/auth")}
              >
                Sign up
              </div>

              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 50, horizontal: -200 }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                ref={menuRef}
                MenuListProps={{
                  sx: {
                    minWidth: 330,
                    minHeight: 290,
                    gap: "20px",
                    borderRadius: "8px",
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "column",
                    paddingLeft: 3,
                    paddingRight: 3,
                    boxShadow: "8px 8px 53.5px 0px #00000014",
                  },
                }}
              >
                <p className="login-menu-title">Clients</p>
                <MenuItem
                  onClick={() => {
                    openLoginPopup();
                    handleMenuClose();
                  }}
                  sx={menuItemStyles}
                >
                  Log in
                </MenuItem>
                <hr style={{ width: "100%", justifySelf: "center" }} />
                <p className="login-menu-title">Lawyers</p>
                <MenuItem
                  onClick={() => {
                    navigate("/grow/email");
                    handleMenuClose();
                  }}
                  sx={menuItemStyles}
                >
                  Log in
                </MenuItem>
              </Menu>
            </div>
          )}
        </div>
      </div>

      {/* Mobile drawer */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
      >
        {drawer}
      </Drawer>
    </div>
  );
};

export default HomeHeader;
